import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { queryClient, queryKeys } from '@/api/query-client';
import type { paths } from '@/api/generated/schema';

/**
 * Les types de paramètres sont DÉRIVÉS du document OpenAPI plutôt que réécrits à la main.
 *
 * Conséquence directe : ajouter un filtre côté C# le rend disponible ici, et en retirer un
 * casse la compilation du frontend au lieu de produire un paramètre silencieusement ignoré
 * par l'API.
 */
type QueryOf<TPath extends keyof paths> = NonNullable<
  paths[TPath] extends { get: { parameters: { query?: infer TQuery } } } ? TQuery : never
>;

export type ScreensQuery = QueryOf<'/api/v1/screens'>;
export type ProjectsQuery = QueryOf<'/api/v1/projects'>;
export type TemplatesQuery = QueryOf<'/api/v1/templates'>;
export type BroadcastsQuery = QueryOf<'/api/v1/broadcasts'>;
export type InvoicesQuery = QueryOf<'/api/v1/billing/invoices'>;
export type NotificationsQuery = QueryOf<'/api/v1/notifications'>;

/**
 * Écrans du compte, avec leur état de connexion.
 *
 * `refetchInterval` permet d'accélérer ponctuellement le rafraîchissement — pendant un
 * appairage, l'utilisateur attend de voir son écran passer « en ligne » dans les secondes
 * qui suivent la saisie du code, pas à la minute suivante.
 */
export function useScreens(params: ScreensQuery = {}, refetchInterval = 60_000) {
  return useQuery({
    queryKey: queryKeys.screens(params),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/screens', { params: { query: params } });

      return data!;
    },

    // L'état d'un écran est une donnée quasi temps réel : le tableau de bord doit refléter
    // rapidement un écran qui vient de tomber. Le heartbeat côté appareil est d'une minute,
    // on interroge donc à la même cadence.
    staleTime: 15_000,
    refetchInterval,
  });
}

/** Valeur de `VideoProjectStatus.Rendering` côté API. */
const PROJECT_STATUS_RENDERING = 1;

/**
 * Projets du locataire, pour « Mes vidéos ».
 *
 * Sondage UNIQUEMENT tant qu'un rendu est en cours. Sans lui, une carte lancée en rendu
 * resterait sur « Rendu en cours » jusqu'à un rechargement manuel : le client global fixe
 * `staleTime` à 30 s et désactive `refetchOnWindowFocus`, donc rien ne vient rafraîchir une
 * page qui reste ouverte — or c'est précisément ce que fait l'utilisateur qui attend sa
 * vidéo. Le sondage s'arrête de lui-même dès qu'aucun projet n'est en rendu, pour ne pas
 * interroger l'API en boucle sur une bibliothèque au repos.
 */
export function useProjects(params: ProjectsQuery = {}) {
  return useQuery({
    queryKey: queryKeys.projects(params),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects', { params: { query: params } });

      return data!;
    },

    refetchInterval: (query) =>
      query.state.data?.items.some((item) => item.status === PROJECT_STATUS_RENDERING) ? 5_000 : false,
  });
}

export function useTemplates(params: TemplatesQuery = {}) {
  return useQuery({
    queryKey: queryKeys.templates(params),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/templates', { params: { query: params } });

      return data!;
    },

    // La bibliothèque de templates est un actif de plateforme : elle bouge très peu.
    staleTime: 10 * 60_000,
  });
}

export function useBroadcasts(params: BroadcastsQuery = {}) {
  return useQuery({
    queryKey: queryKeys.broadcasts(params),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/broadcasts', { params: { query: params } });

      return data!;
    },
  });
}

export function useSubscription() {
  return useQuery({
    queryKey: queryKeys.subscription,

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/billing/subscription');

      return data!;
    },
  });
}

/**
 * Résilie l'abonnement en fin de période (ou, si la formule impose un engagement, en fin
 * d'engagement — voir `Subscription.EffectiveCancellationDate` côté backend). Le service
 * reste actif jusqu'à cette date, l'appel Stripe n'étant transmis qu'au bon moment.
 */
export function useCancelSubscription() {
  return useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/billing/subscription/cancel');
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscription });
    },
  });
}

/** Annule une résiliation en cours : l'abonnement reprend son renouvellement normal. */
export function useResumeSubscription() {
  return useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/billing/subscription/resume');
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscription });
    },
  });
}

export function useInvoices(params: InvoicesQuery = {}) {
  return useQuery({
    queryKey: queryKeys.invoices(params),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/billing/invoices', { params: { query: params } });

      return data!;
    },
  });
}

/** Catalogue des exploits et progression du compte (XP total, déblocages). */
export function useExploits() {
  return useQuery({
    queryKey: queryKeys.exploits,

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/exploits');

      return data!;
    },
  });
}

export function usePlans() {
  return useQuery({
    queryKey: queryKeys.plans,

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/billing/plans');

      return data!;
    },

    staleTime: 30 * 60_000,
  });
}

export function useNotifications(params: NotificationsQuery = {}, options: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: queryKeys.notifications(params),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/notifications', { params: { query: params } });

      return data!;
    },

    refetchInterval: options.refetchInterval ?? 120_000,
  });
}

/** Marque une notification comme lue. Restreint à son destinataire côté backend. */
export function useMarkNotificationRead() {
  return useMutation({
    mutationFn: async (notificationId: string) => {
      await api.POST('/api/v1/notifications/{notificationId}/read', {
        params: { path: { notificationId } },
        // FastEndpoints répond 415 sans Content-Type + corps JSON valide, même si le contrat
        // OpenAPI ne déclare aucun corps pour cette route (même remarque que `useMarkConversationRead`).
        // @ts-expect-error — le contrat OpenAPI ne déclare pas de corps pour cette route.
        body: {},
      });
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications() });
    },
  });
}

/** Marque toutes les notifications de l'utilisateur comme lues, en une seule requête. */
export function useMarkAllNotificationsRead() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/notifications/read-all');

      return data!;
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications() });
    },
  });
}
